import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Facebook, Instagram, Linkedin } from "lucide-react";
import { openBooking } from "./booking/BookingDrawerHost";

import "./site.css";

const API = process.env.REACT_APP_BACKEND_URL;
const UTM = (href, id) => `${href}${href.includes("?") ? "&" : "?"}utm_source=email&utm_medium=signature&utm_campaign=toolkit&utm_content=${id}`;

function tap(button, label) {
  let sid = null;
  try { sid = sessionStorage.getItem("hm_sid"); } catch {}
  fetch(`${API}/api/public/start/tap`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ button: `matt-${button}`, label, sid }), keepalive: true }).catch(() => {});
}

function openBook(e) {
  e.preventDefault();
  tap("book", "Book a call");
  openBooking({ path: "/matt/", persona: "matt" });
}

const TOOLS = [
  { id: "renewal-checker", href: "/renewal-checker/", label: "Win your renewal", sub: "Is the bank's offer actually good? Find out in 60 seconds." },
  { id: "payment-relief", href: "/payment-relief/", label: "Get payment relief", sub: "Better cash flow. One lower payment, more breathing room." },
  { id: "first-home-plan", href: "/first-home-plan/", label: "Buy your first home", sub: "Know your magic number before you fall for a kitchen." },
  { id: "yearly-checkup", href: "/tools/yearly-mortgage-checkup/", label: "Yearly mortgage checkup", sub: "Life changed since you signed. Ten minutes to make sure your mortgage kept up." },
  { id: "rates", href: "/rates/", label: "Spy on today’s best rates", sub: "The numbers your bank hopes you won't look up." },
  { id: "vault", href: "/strategy-vault/", label: "The Strategy Vault", sub: "Short reads that answer the questions you'd text a friend in the business." },
];

function Tool({ t, i }) {
  return (
    <Link to={UTM(t.href, t.id)} className="start-btn" style={{ animationDelay: `${160 + i * 60}ms` }} data-testid={`matt-tool-${t.id}`} onClick={() => tap(t.id, t.label)}>
      <span className="start-btn-text"><b>{t.label}</b><small>{t.sub}</small></span>
      <span className="start-btn-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
    </Link>
  );
}

export default function MattPage() {
  useEffect(() => {
    let sid = null;
    try { sid = sessionStorage.getItem("hm_sid"); if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("hm_sid", sid); } } catch {}
    fetch(`${API}/api/public/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ path: "/matt/", sid, ref: document.referrer || null, w: window.innerWidth }) }).catch(() => {});
  }, []);
  return (
    <div className="hm-site start-page matt-page" data-testid="matt-page">
      <Helmet>
        <title>Matt Broom-Hall · Your Mortgage Toolkit · Hello Mortgage Team</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Everything Matt sends clients, in one place. Renewal Checker, Payment Relief, First Home Plan, today's rates and a fifteen-minute call." />
        <meta name="theme-color" content="#14283b" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
      </Helmet>
      <main className="start-wrap">
        <header className="start-head matt-head">
          <div className="start-top">
            <Link to={UTM("/", "logo")} className="start-mark" aria-label="Hello Mortgage home" onClick={() => tap("logo", "Logo")}><img src="/brand/hello-mortgage-team-logo-footer.png" alt="Hello Mortgage Team" width="200" height="67" /></Link>
            <span className="start-handle">hellomortgage.ca/matt</span>
          </div>
          <div className="matt-id">
            <img className="matt-photo" src="/brand/matt-broom-hall.png" alt="Matt Broom-Hall" width="120" height="120" data-testid="matt-photo" />
            <div>
              <div className="start-hello" aria-hidden="true">hello,</div>
              <h1 className="start-h1 matt-h1" data-testid="matt-name">I'm Matt.</h1>
              <p className="start-sub matt-sub" data-testid="matt-title">Founder &amp; Mortgage Broker<br />Hello Mortgage Team<br />Powered by BRX Mortgage</p>
              <div className="matt-contact" data-testid="matt-contact">
                <a href="/book/" onClick={openBook} className="matt-chip matt-chip-primary" data-testid="matt-book"><CalendarDays size={16} /> Book a call</a>
              </div>
            </div>
          </div>
        </header>
        <div className="start-lead" style={{ marginTop: 6 }}>Your mortgage toolkit</div>
        <nav className="start-btns" aria-label="Mortgage toolkit" data-testid="matt-tools">
          {TOOLS.map((t, i) => <Tool key={t.id} t={t} i={i} />)}
        </nav>
        <footer className="start-foot">
          <Link to={UTM("/", "explore")} className="start-explore" onClick={() => tap("explore", "Explore")} data-testid="matt-explore">Explore the full site</Link>
          <span className="start-foot-home">Edmonton is home. Alberta is our playground.</span>
        </footer>
        <div className="matt-social" data-testid="matt-social">
          <a href="https://www.instagram.com/hellomortgage.ca/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" onClick={() => tap("instagram", "Instagram")} data-testid="matt-instagram"><Instagram size={20} /></a>
          <a href="https://www.facebook.com/hellomortgage.ca" target="_blank" rel="noopener noreferrer" aria-label="Facebook" onClick={() => tap("facebook", "Facebook")} data-testid="matt-facebook"><Facebook size={20} /></a>
          <a href="https://www.linkedin.com/in/mattbroom-hall/" target="_blank" rel="noopener noreferrer" aria-label="Matt on LinkedIn" onClick={() => tap("linkedin", "LinkedIn")} data-testid="matt-linkedin"><Linkedin size={20} /></a>
        </div>

      </main>
    </div>
  );
}
